import Fade from '@mui/material/Fade';
import { m } from 'motion/react';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Col, Row } from 'react-bootstrap';
import styled from 'styled-components';
import Badge from '../../molecules/Badge/Badge';
import BadgeContainer from '../../molecules/Badge/Container/BadgeContainer';
import Pagination from '../../molecules/Pagination/Pagination';
import { colors } from '../../styles/atoms/colors';
import { decamelize, toCamelCase } from '../../utils/helperFunctions';
import { getParams, updateParamsSilently } from '../../utils/url/handleParams';
import DropdownMenu from '../CaseStudyList/DropdownMenu';
import NewsRoomCard from './NewsRoomCard';

const NewsRoomList = ({ news = [] }) => {
  const articleList = useRef(null);
  const [filteredNews, setFilteredNews] = useState(news);
  const [currentCategory, setCurrentCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const pageLimit = 9;
  const currentPageArticles =
    filteredNews &&
    filteredNews.slice((page - 1) * pageLimit, page * pageLimit);
  const totalPage = Math.ceil(filteredNews.length / pageLimit);

  const articleCards = useMemo(
    () =>
      currentPageArticles.map(article => (
        <m.div
          className="col-md-4 newsroom-col"
          key={article.id}
          initial={{ opacity: 0, scale: 0, filter: 'blur(5px)' }}
          animate={{ opacity: 1, scale: 1, filter: 'blur(0)' }}
          exit={{ filter: 'blur(5px)', opacity: 0, scale: 0 }}
          transition={{ type: 'tween', delay: 0.1, duration: 0.5 }}
        >
          <NewsRoomCard
            title={article.title}
            excerpt={article.excerpt}
            category={article.category?.name}
            date={article.datePublished}
            brandLogo={article.brandLogo}
            newsImage={article.featuredImage}
            slug={article.slug}
          />
        </m.div>
      )),
    [currentPageArticles]
  );

  useEffect(() => {
    const params = getParams(['category', 'p']);

    params?.category[0] && setCurrentCategory(decamelize(params.category[0]));

    params?.p[0] && setPage(parseInt(params.p[0], 10));
  }, []);

  useEffect(() => {
    setFilteredNews(
      news?.filter(
        article =>
          (currentCategory === 'All' ||
            article.category?.name === currentCategory) &&
          article.title.toLowerCase().includes(search.toLowerCase())
      )
    );
  }, [news, currentCategory, search]);

  const categories = useMemo(() => {
    const categorySet = new Set();

    news.forEach(article => categorySet.add(article.category.name));

    return [...categorySet];
  }, [news]);

  const goPage = pageNumber => {
    setPage(pageNumber);
    updateParamsSilently('p', pageNumber, 'replace', 'articles', true);

    if (articleList.current) {
      articleList.current
        .closest('section')
        .scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  const handleCategoryChange = useCallback(selectedCategory => {
    setCurrentCategory(selectedCategory);
    setSearch('');
    setPage(1);
    updateParamsSilently('p', '', 'remove', '', true);

    if (selectedCategory === 'All') {
      updateParamsSilently('category', '', 'remove', '', true);
    } else {
      updateParamsSilently(
        'category',
        toCamelCase(selectedCategory),
        'replace',
        'articles',
        true
      );
    }
  }, []);

  return (
    <NewsRoomListContainer>
      <Row className="controls mx-0">
        <Col className="cat-list px-0">
          <div className="d-block d-md-none">
            <DropdownMenu
              isBold
              isMedium
              selected={currentCategory}
              items={categories}
              handleChangeSelected={handleCategoryChange}
            />
          </div>
          <div className="d-md-flex d-none">
            <BadgeContainer size="lg">
              <Badge
                className="badge"
                size="sm"
                badgeColor={
                  currentCategory === 'All'
                    ? colors.primary[50]
                    : colors.base.white
                }
                textColor={
                  currentCategory === 'All'
                    ? colors.primary[500]
                    : colors.gray[800]
                }
                onClick={() => handleCategoryChange('All')}
              >
                All
              </Badge>
              {categories.map(category => (
                <Badge
                  className="badge"
                  size="sm"
                  badgeColor={
                    currentCategory === category
                      ? colors.primary[50]
                      : colors.base.white
                  }
                  textColor={
                    currentCategory === category
                      ? colors.primary[500]
                      : colors.gray[800]
                  }
                  onClick={() => handleCategoryChange(category)}
                >
                  {category}
                </Badge>
              ))}
            </BadgeContainer>
          </div>
        </Col>
        <Col sm="auto" className="px-0">
          <input
            type="text"
            name="search"
            id="search-box"
            className="form-control mb-4"
            placeholder="Search..."
            value={search}
            onChange={e => {
              setCurrentCategory('All');
              setSearch(e.target.value);
            }}
          />
        </Col>
      </Row>
      <Row ref={articleList}>
        {news && news.length > 0 && (
          <>
            {articleCards}
            {totalPage > 1 && (
              <Pagination
                totalPage={totalPage}
                currentPage={page}
                goPage={goPage}
              />
            )}
          </>
        )}
        <Fade in={news && news.length <= 0} mountOnEnter unmountOnExit>
          <h2 className="text-center w-100" style={{ padding: '96px 0' }}>
            No available data to show
          </h2>
        </Fade>
      </Row>
    </NewsRoomListContainer>
  );
};

export const NewsRoomListContainer = styled.div`
  .controls {
    align-items: flex-end;
  }

  .cat-tag {
    margin-right: 30px;
    display: inline-block;
    padding: 2px 5px;
    font-size: 18px;
    font-weight: 600;
    border-radius: 2px;
    cursor: pointer;

    &.active {
      background: rgba(28, 63, 255, 0.1);
      color: var(--primary-color);
    }
  }

  .cat-list {
    margin-bottom: 40px;

    @media (max-width: 768px) {
      margin-bottom: 16px;
    }
  }

  .badge {
    cursor: pointer;
    font-size: 0.75rem;
    line-height: 1;
    font-weight: 400;
    padding: 6px 8px;
  }

  #search-box {
    font-size: 18px;
    padding-left: 44px;
    background-image: url('data:image/svg+xml,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20viewBox%3D%220%200%20512%20512%22%3E%3Cpath%20fill%3D%22%237B848F%22%20d%3D%22M508.5%20481.6l-129-129c-2.3-2.3-5.3-3.5-8.5-3.5h-10.3C395%20312%20416%20262.5%20416%20208%20416%2093.1%20322.9%200%20208%200S0%2093.1%200%20208s93.1%20208%20208%20208c54.5%200%20104-21%20141.1-55.2V371c0%203.2%201.3%206.2%203.5%208.5l129%20129c4.7%204.7%2012.3%204.7%2017%200l9.9-9.9c4.7-4.7%204.7-12.3%200-17zM208%20384c-97.3%200-176-78.7-176-176S110.7%2032%20208%2032s176%2078.7%20176%20176-78.7%20176-176%20176z%22%3E%3C%2Fpath%3E%3C%2Fsvg%3E');
    background-repeat: no-repeat;
    background-position: 16px center;
    background-size: 16px;
    margin-bottom: 45px !important;

    &:hover {
      border-color: #0015ca;
    }

    &:focus {
      box-shadow: none;
      border-color: var(--primary-color);
      background-color: #f9fbfe;
    }
  }
`;

export default NewsRoomList;
